import { useAtomValue } from 'jotai';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { AccessibilityData, AuditData } from '@eye-able/types';
import { currentAuditEntryAtom } from '@/global-store';
import calculateAccessibilityData from '@/helpers/calculateAccessibilityData';
import { queryKeys, reportStorageUrl } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useFetchRules } from '@/http/queries/useFetchRules';

interface FetchAccessibilityDataOptions {
  auditEntry?: unknown;
  wcagLevel?: unknown;
}

export interface AccessibilityDataQueryResult {
  data: AccessibilityData | undefined;
  isLoading: boolean;
  isError: boolean;
}

const offlineMode = import.meta.env.VITE_OFFLINE_MODE === 'true';

function useFetchReportAndHistory(auditEntry: AuditData | null) {
  const cdnDirectory = auditEntry?.cdnDirectory;
  const fullCdnPath = cdnDirectory
    ? reportStorageUrl + cdnDirectory + (cdnDirectory.endsWith('/') ? '' : '/')
    : null;

  const queries = useQueries({
    queries: [
      {
        enabled: Boolean(fullCdnPath),
        queryKey: [queryKeys.REPORT, fullCdnPath],
        retryOnMount: false,
        queryFn: async () => {
          if (offlineMode) {
            return (await import('../../../mockData/report.json')).default;
          }

          return fetchWrapper.get<AccessibilityData['report']>(
            `${fullCdnPath}report.json`,
          );
        },
      },
      {
        enabled: Boolean(fullCdnPath),
        queryKey: [queryKeys.HISTORY, fullCdnPath],
        retryOnMount: false,
        queryFn: async () => {
          if (offlineMode) {
            return (await import('../../../mockData/history.json')).default;
          }

          return fetchWrapper.get<AccessibilityData['history']>(
            `${fullCdnPath}history.json`,
          );
        },
      },
    ],
  });

  return {
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
    report: queries[0].data,
    history: queries[1].data,
    reportUpdatedAt: queries[0].dataUpdatedAt,
    historyUpdatedAt: queries[1].dataUpdatedAt,
  };
}

export function useFetchAccessibilityData(
  options: FetchAccessibilityDataOptions = {},
): AccessibilityDataQueryResult {
  const currentAuditEntry = useAtomValue(currentAuditEntryAtom);
  const auditEntry = (options.auditEntry ??
    currentAuditEntry) as AuditData | null;
  const conformanceTarget =
    typeof options.wcagLevel === 'string'
      ? options.wcagLevel
      : auditEntry?.complianceTarget;

  const {
    report,
    history,
    reportUpdatedAt,
    historyUpdatedAt,
    isLoading: reportAndHistoryLoading,
    isError: reportAndHistoryError,
  } = useFetchReportAndHistory(auditEntry);
  const {
    wcagRules,
    eaRules,
    language: rulesLanguage,
    rulesUpdatedAt,
    isLoading: rulesLoading,
    isError: rulesError,
  } = useFetchRules();

  const accessibilityDataQuery = useQuery({
    enabled:
      Boolean(conformanceTarget) &&
      !rulesLoading &&
      !rulesError &&
      !reportAndHistoryLoading &&
      !reportAndHistoryError,
    queryKey: [
      queryKeys.ACCESSIBILITY_DATA,
      auditEntry?.cdnDirectory,
      conformanceTarget,
      rulesLanguage,
      rulesUpdatedAt,
      reportUpdatedAt,
      historyUpdatedAt,
    ],
    queryFn: () =>
      calculateAccessibilityData({
        report,
        history,
        conformanceTarget,
        eaRules,
        wcagRules,
        auditEntry,
      }) as AccessibilityData,
    staleTime: Infinity,
  });

  const data = accessibilityDataQuery.data;

  return {
    data,
    isLoading:
      rulesLoading ||
      reportAndHistoryLoading ||
      accessibilityDataQuery.isLoading ||
      (!data && !reportAndHistoryError && !rulesError),
    isError:
      reportAndHistoryError || rulesError || accessibilityDataQuery.isError,
  };
}
